import React, { memo } from 'react';
import * as LucideIcons from 'lucide-react';
import dynamic from 'next/dynamic';
import dynamicIconImports from 'lucide-react/dynamicIconImports';

const preloadedIcons = [
  'house', 'user', 'settings', 'calendar', 'plus', 'minus', 'trash-2',
  'check', 'x', 'search', 'filter', 'list', 'grid', 'menu', 'users-round',
  'table', 'kanban', 'file-text', 'banknote', 'list-collapse', 'sparkles',
  'list-minus', 'square', 'hash', 'timer', 'user-round', 'bug', 'pen-line',
  'file', 'life-buoy', 'chevron-up', 'chevron-down', 'chevron-left', 
  'chevron-right', 'ellipsis', 'heading-1', 'heading-2', 'heading-3', 
  'heading-4', 'heading-5', 'heading-6', 'table-cells-split', 'heading', 
  'list-ordered', 'list-todo', 'code', 'quote', 'link', 'image', 'bold', 
  'italic', 'underline', 'strikethrough', 'align-left', 'align-center', 
  'align-right'
];

const MemoizedPreloadedIcons = preloadedIcons.reduce((acc, iconName) => {
  const variations = [
    iconName,
    `${iconName}Icon`,
    iconName.charAt(0).toUpperCase() + iconName.slice(1),
    `${iconName.charAt(0).toUpperCase() + iconName.slice(1)}Icon`
  ];

  for (const variation of variations) {
    const IconComponent = LucideIcons[variation];
    if (IconComponent) {
      const MemoizedIcon = memo(({ ...props }) => (
        <IconComponent {...props} />
      ));
      MemoizedIcon.displayName = `Memoized${iconName.charAt(0).toUpperCase() + iconName.slice(1)}Icon`;
      acc[iconName.toLowerCase()] = MemoizedIcon;
      break;
    }
  }

  return acc;
}, {});

// Memoized dynamic icon loading
const DynamicIcon = memo(({ name, ...props }) => {
  const LucideIcon = dynamic(dynamicIconImports[name], { 
    ssr: false,
    loading: () => (
      <div style={{ width: props.size || 24, height: props.size || 24 }} />
    )
  });

  return <LucideIcon {...props} />;
});

DynamicIcon.displayName = 'DynamicIcon';

const Icon = ({ name, ...props }) => {
  const normalizedName = name.toLowerCase();
  const PreloadedIconComponent = MemoizedPreloadedIcons[normalizedName];

  if (PreloadedIconComponent) {
    return <PreloadedIconComponent {...props} />;
  }

  return <DynamicIcon name={name} {...props} />;
};

Icon.availableIcons = Object.keys(MemoizedPreloadedIcons);

export default memo(Icon);